import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, ExternalLink, Send } from "lucide-react";
import { CONTACT } from "../content";
import { Eyebrow, HudCorners, Reveal } from "./bits";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-[#F2F5F9] border border-[#CBD3DF] rounded-sm px-4 py-3 text-base text-[#0B0D10] placeholder-slate-400 focus:border-emerald/70 focus:ring-1 focus:ring-emerald/30 transition-colors duration-300";

const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        service: form.service || t.form.services[0],
        message: form.message,
        language: lang
      });
      toast.success(t.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  const infoRows = [
    { icon: MapPin, label: t.addressLabel, value: CONTACT.address, testid: "contact-info-address" },
    { icon: Phone, label: t.phoneLabel, value: CONTACT.phone, href: CONTACT.phoneHref, testid: "contact-info-phone" },
    { icon: Mail, label: t.emailLabel, value: CONTACT.email, href: CONTACT.emailHref, testid: "contact-info-email" }
  ];

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 lg:py-36 tactical-grid">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow>{t.eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display font-bold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0D10]">
            {t.title}
          </h2>
          <p className="mt-5 max-w-xl text-base sm:text-lg text-neutral-700">{t.subtitle}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-5 gap-10 lg:gap-14">
          <Reveal className="lg:col-span-3">
            <form data-testid="contact-form" onSubmit={submit} className="relative bg-[#FFFFFF] border border-[#CBD3DF] rounded-sm p-7 sm:p-9 space-y-5">
              <HudCorners />
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-form-name-input"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.form.name}
                  className={inputCls}
                />
                <input
                  data-testid="contact-form-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.form.email}
                  className={inputCls}
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <input
                  data-testid="contact-form-phone-input"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder={t.form.phone}
                  className={inputCls}
                />
                <select
                  data-testid="contact-form-service-select"
                  value={form.service}
                  onChange={set("service")}
                  className={`${inputCls} appearance-none cursor-pointer ${form.service ? "" : "text-neutral-600"}`}
                >
                  <option value="" disabled>
                    {t.form.service}
                  </option>
                  {t.form.services.map((s) => (
                    <option key={s} value={s} className="bg-[#FFFFFF] text-[#0B0D10]">
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                data-testid="contact-form-message-input"
                required
                minLength={5}
                rows={5}
                value={form.message}
                onChange={set("message")}
                placeholder={t.form.message}
                className={`${inputCls} resize-none`}
              />
              <button
                data-testid="contact-form-submit-button"
                type="submit"
                disabled={sending}
                className="flex items-center justify-center gap-2 w-full sm:w-auto bg-emerald text-[#E9EDF3] font-display font-bold uppercase tracking-wider px-10 py-3.5 rounded-sm hover:bg-emerald-dim disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {sending ? t.form.sending : t.form.submit}
                <Send size={15} strokeWidth={2.5} />
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2">
            <div className="relative h-full bg-[#FFFFFF] border border-[#CBD3DF] rounded-sm p-7 sm:p-9">
              <HudCorners />
              <h3 className="font-display font-bold uppercase tracking-wider text-xl text-[#0B0D10]">
                {t.infoTitle}
              </h3>
              <div className="mt-8 space-y-7">
                {infoRows.map((row) => (
                  <div key={row.testid} data-testid={row.testid} className="flex gap-4">
                    <span className="shrink-0 w-10 h-10 flex items-center justify-center border border-emerald/30 bg-emerald/5 rounded-sm text-emerald">
                      <row.icon size={17} />
                    </span>
                    <div>
                      <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                        {row.label}
                      </div>
                      {row.href ? (
                        <a href={row.href} className="mt-1 block text-base text-neutral-900 hover:text-emerald transition-colors">
                          {row.value}
                        </a>
                      ) : (
                        <div className="mt-1 text-base leading-relaxed text-neutral-900">{row.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <a
                data-testid="contact-info-map-link"
                href={CONTACT.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-10 inline-flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.2em] text-emerald hover:text-[#0B0D10] transition-colors"
              >
                {t.mapLink}
                <ExternalLink size={14} />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
